import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './HelpScreen.less';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import MarkdownNavigationSidebar from './components/MarkdownNavigationSidebar/MarkdownNavigationSidebar';
import MarkdownHelpContent from './components/MarkdownHelpContent/MarkdownHelpContent';
import MarkdownViewer from '../../components/envago/MarkdownViewer/MarkdownViewer';
import BasicScreen from '../common/BasicScreen/BasicScreen';
import Collapsable from '../../components/envago/Collapsable/Collapsable';
import RoundIconButton from '../../components/envago/Button/RoundIconButton';
import Icon from '../../components/envago/Icon/Icon';
import { applicationSelectors } from '../../redux/application';
import { helpActions, helpSelectors } from '../../redux/help';
import { languageSelectors } from '../../redux/language';
import { Helmet } from 'react-helmet-async';

type HelpLoadState = 'loading' | 'loaded' | 'error_language' | 'error_offline' | 'error_generic';

const HelpScreen = () => {
    const dispatch = useDispatch();
    const applicationId = useSelector(applicationSelectors.getApplicationId);
    const isGlobalHelpEnabled = useSelector(helpSelectors.isGlobalHelpEnabled);
    const currentLanguage = useSelector(languageSelectors.getCurrentLanguage);
    const cachedHelpContent = useSelector(helpSelectors.getGlobalHelpContent);

    const cachedMarkdown = currentLanguage
        ? (cachedHelpContent?.[currentLanguage] ??
           Object.values(cachedHelpContent ?? {}).find((v) => v.length > 10))
        : undefined;

    const [markdownText, setMarkdownText] = useState(cachedMarkdown ?? '');
    const [loadState, setLoadState] = useState<HelpLoadState>(cachedMarkdown ? 'loaded' : 'loading');
    const [tocOpen, setTocOpen] = useState(false);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (!currentLanguage) return;
        if (cachedMarkdown) {
            setLoadState('loaded');
            setMarkdownText(cachedMarkdown);
        } else {
            setLoadState('loading');
            setMarkdownText('');
        }
        axios
            .get(`/api/public/help/${applicationId}/${currentLanguage}`)
            .then((res) => {
                if (res.data.markdown) {
                    setMarkdownText(res.data.markdown);
                    setLoadState('loaded');
                    dispatch(helpActions.setGlobalHelpContent(currentLanguage, res.data.markdown));
                } else {
                    if (!cachedMarkdown) {
                        setLoadState('error_language');
                    }
                }
            })
            .catch((error) => {
                if (cachedMarkdown) {
                    return;
                }
                if (!error.response) {
                    setLoadState('error_offline');
                } else if (error.response.status === 404) {
                    setLoadState('error_language');
                } else {
                    setLoadState('error_generic');
                }
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, currentLanguage]);

    useEffect(() => {
        const handleScroll = () => setShowBackToTop(window.scrollY > 200);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const applicationName = useSelector(applicationSelectors.getApplicationName);

    if (!isGlobalHelpEnabled) return null;

    const hasHeadings = markdownText ? /^#{1,2} /m.test(markdownText) : false;

    const getHelpContent = (): string | undefined => {
        if (loadState === 'loaded') return markdownText;
        if (cachedMarkdown) return cachedMarkdown;
        if (loadState === 'error_language') return t('help.global.defaultText');
        if (loadState === 'error_offline') return t('help.global.offlineText');
        if (loadState === 'error_generic') return t('help.global.errorText');
        return undefined;
    };

    const mobileToc = hasHeadings ? (
        <div className={'md:hidden mb-4'}>
            <Collapsable
                className={'bg-gray-200 shadow-md p-2 rounded font-medium'}
                triggerText={t('help.global.tableOfContents')}
                open={tocOpen}
                onChange={setTocOpen}
                arrowPosition={'right'}
            >
                <MarkdownNavigationSidebar text={markdownText} mobile />
            </Collapsable>
        </div>
    ) : null;

    return (
        <BasicScreen>
            <Helmet>
                <title>{applicationName} | Hilfe</title>
            </Helmet>
            <div className={'flex gap-4'}>
                <div className={'hidden md:block w-3/12 flex-initial'}>
                    <MarkdownNavigationSidebar className={'fixed top-20 w-3/12 lg:w-64 xl:w-72 2xl:w-80'} text={loadState === 'loaded' ? markdownText : ''} />
                </div>
                <div className={'flex-1 w-9/12 max-w-lg'}>
                    <MarkdownHelpContent
                        className={'h-full mx-4'}
                        mobileNav={mobileToc}
                        children={<MarkdownViewer text={getHelpContent()} />}
                    />
                </div>
            </div>
            {showBackToTop && hasHeadings && (
                <div className={'md:hidden fixed bottom-4 right-4 z-10'}>
                    <RoundIconButton
                        secondary
                        size={44}
                        aria={{ 'aria-label': t('help.global.backToTop') }}
                        onClick={() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            setTocOpen(true);
                        }}
                        icon={<Icon path={Icon.Path.mdiArrowUp} className={'w-5 h-5'} />}
                    />
                </div>
            )}
        </BasicScreen>
    );
};

export default HelpScreen;
